import { useTranslation } from "react-i18next";
import { DEFAULT_POLICIES, type PolicyDoc, type PolicyKey } from "@/lib/policies-store";

/**
 * Applies i18n to a policy doc. If the stored doc matches the built-in default
 * (i.e. admin has not customized), we swap in translated title/description and
 * per-section title/body. Custom admin content is kept as-authored.
 */
export function useLocalizedPolicy(key: PolicyKey, doc: PolicyDoc): PolicyDoc {
  const { t, i18n } = useTranslation();
  if (i18n.language === "en") return doc;

  const defaults = DEFAULT_POLICIES[key];
  const isDefault =
    doc.title === defaults.title &&
    doc.description === defaults.description &&
    doc.sections.length === defaults.sections.length &&
    doc.sections.every((s, i) => s.body === defaults.sections[i].body);

  if (!isDefault) return doc;

  const translated = t(`policies.${key}`, { returnObjects: true }) as {
    title: string;
    description: string;
    sections: { title: string; body: string }[];
  };

  return {
    ...doc,
    title: translated.title,
    description: translated.description,
    sections: doc.sections.map((s, i) => ({
      id: s.id,
      title: translated.sections[i]?.title ?? s.title,
      body: translated.sections[i]?.body ?? s.body,
    })),
  };
}
